import { useRef } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useWidth } from '@hotjar/insights-common';
import { clamp } from '@hotjar/insights-common/lib/number';
import { HEATMAP_WIDTH_PER_DEVICE } from '../../constants-types/HeatmapWidthPerDevice';
import { Layer } from '../Layer/Layer';
import { EngagementMarker } from './EngagementMarker';

const EngagementMarkersContainer = Layer.withComponent(styled.div<{ zoom: number; height: number }>`
  position: relative;
  width: 100%;

  ${({ height, zoom }) => `
    max-height: ${height}px;
    zoom: ${zoom};
  `}
`);

const EngagementZone = styled.div<{
  position: {
    top: number;
    left: number;
    size: number;
  };
}>`
  position: absolute;
  opacity: 0;
  box-sizing: border-box;
  border: 2px solid ${tokens.color.ui_border_active};
  ${({ position }) => `
    left: ${position.left}px;
    top: ${position.top}px;
    width: ${position.size}px;
    height: ${position.size}px;
  `};

  &:hover {
    opacity: 1;
  }
`;

const calculateZonePosition = (
  zone: HeatmapAPI.EngagementZone,
  zoneSize: number,
  scaleRatio: number,
) => {
  return {
    top: zone.row * zoneSize * scaleRatio,
    left: zone.col * zoneSize * scaleRatio,
    size: zoneSize * scaleRatio,
  };
};

export const EngagementMarkersLayer = ({
  selectedDeviceType,
  engagementZones,
  isEngagementZonesActive,
}: {
  selectedDeviceType: HeatmapDeviceTypes;
  engagementZones: HeatmapAPI.GetEngagementZonesData | undefined;
  isEngagementZonesActive: boolean;
}): JSX.Element | null => {
  const ref = useRef<HTMLDivElement>(null);
  const [actualWidth] = useWidth(ref, engagementZones?.imageSize[0]);

  if (
    !isEngagementZonesActive ||
    engagementZones === undefined ||
    engagementZones.engagementGrid.length === 0
  ) {
    return null;
  }

  const { zoneSize } = engagementZones;
  const width = engagementZones.imageSize[0];
  const height = engagementZones.imageSize[1];
  const zoom = HEATMAP_WIDTH_PER_DEVICE[selectedDeviceType] / width;
  const scaleRatio = clamp(actualWidth / width, 0, 1);

  return (
    <EngagementMarkersContainer zoom={zoom} height={height} ref={ref}>
      {engagementZones.engagementGrid.map((zone) => {
        const zonePosition = calculateZonePosition(zone, zoneSize, scaleRatio);

        return (
          <EngagementZone key={`${zone.row}_${zone.col}`} position={zonePosition}>
            <EngagementMarker
              gridWidth={actualWidth}
              zonePosition={zonePosition}
              score={zone.score}
            />
          </EngagementZone>
        );
      })}
    </EngagementMarkersContainer>
  );
};
